import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Separator } from '../ui/separator';
import { GitBranch, Clock, FileText, Loader2, RotateCcw, Check, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import axiosInstance from '../../utils/axios-config';
import { toast } from 'sonner';

/**
 * DiscussionVersionTabs Component
 *
 * Displays version history for discussions with tabs to view and restore versions.
 *
 * Features:
 * - View version timeline with metadata
 * - Restore old versions with confirmation
 * - Show version status (Current, On-Chain, Pending, Failed)
 * - Display edit reasons and content diffs
 *
 * @param {Object} props
 * @param {string} props.discussionOid - Discussion OID
 * @param {number} props.currentVersion - Current version number
 * @param {function} props.onVersionRestore - Callback when version is restored (optional)
 */
export function DiscussionVersionTabs({
  discussionOid,
  currentVersion,
  onVersionRestore,
}) {
  const [activeTab, setActiveTab] = useState('current');
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [error, setError] = useState(null);

  // Load version history when History tab is opened
  useEffect(() => {
    if (activeTab === 'history' && versions.length === 0) {
      loadVersionHistory();
    }
  }, [activeTab, discussionOid]);

  const loadVersionHistory = async () => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/omniverse/discussion/${discussionOid}/versions?limit=50&offset=0`
      );

      if (response.data.success) {
        setVersions(response.data.versions || []);
      } else {
        setError('Failed to load version history');
      }
    } catch (err) {
      console.error('[VersionTabs] Error loading history:', err);
      setError(err.response?.data?.error || 'Failed to load version history');
      toast.error('Failed to load version history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadSpecificVersion = async (versionNumber) => {
    setIsLoadingVersion(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/omniverse/discussion/${discussionOid}/versions/${versionNumber}`
      );

      if (response.data.success) {
        setSelectedVersion(response.data.version);
      } else {
        setError('Failed to load version');
      }
    } catch (err) {
      console.error('[VersionTabs] Error loading version:', err);
      setError(err.response?.data?.error || 'Failed to load version');
      toast.error('Failed to load version');
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    setIsRestoring(true);
    try {
      const response = await axiosInstance.post(
        `/omniverse/discussion/${discussionOid}/restore-version`,
        { versionNumber }
      );

      if (response.data.success) {
        toast.success(`Version ${versionNumber} restored successfully`);
        setShowRestoreConfirm(false);
        setVersionToRestore(null);

        // Reload version history
        await loadVersionHistory();

        // Notify parent component to refresh discussion
        if (onVersionRestore) {
          onVersionRestore(versionNumber);
        }
      } else {
        toast.error('Failed to restore version');
      }
    } catch (err) {
      console.error('[VersionTabs] Error restoring version:', err);
      toast.error(err.response?.data?.error || 'Failed to restore version');
    } finally {
      setIsRestoring(false);
    }
  };

  const openRestoreConfirm = (version) => {
    setVersionToRestore(version);
    setShowRestoreConfirm(true);
  };

  const getVersionBadge = (version) => {
    if (version.versionNumber === currentVersion) {
      return (
        <Badge variant="default" className="bg-green-500 hover:bg-green-600">
          <Check className="h-3 w-3 mr-1" />
          Current
        </Badge>
      );
    }

    if (version.mintState === 'FINALIZED') {
      return (
        <Badge variant="secondary">
          <GitBranch className="h-3 w-3 mr-1" />
          On-Chain
        </Badge>
      );
    }

    if (version.mintState === 'PENDING') {
      return (
        <Badge variant="outline">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Pending
        </Badge>
      );
    }

    if (version.mintState === 'FAILED') {
      return (
        <Badge variant="destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    }

    return null;
  };

  const formatCharacterChange = (charactersChanged) => {
    if (!charactersChanged) return '';
    const sign = charactersChanged > 0 ? '+' : '';
    return `${sign}${charactersChanged} characters`;
  };

  return (
    <div className="my-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="current">
            <FileText className="h-4 w-4 mr-2" />
            Current
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="h-4 w-4 mr-2" />
            History ({currentVersion > 1 ? currentVersion : 0} versions)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Current Version</CardTitle>
              <CardDescription>
                You are viewing version {currentVersion} of this discussion.
              </CardDescription>
            </CardHeader>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {isLoadingHistory ? (
            <Card>
              <CardContent className="pt-6 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </CardContent>
            </Card>
          ) : error ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-red-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>{error}</p>
                </div>
              </CardContent>
            </Card>
          ) : versions.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  No version history available yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Version History</CardTitle>
                <CardDescription>
                  View and restore previous versions of this discussion.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {versions.map((version, index) => (
                  <div key={version.versionNumber}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            Version {version.versionNumber}
                          </span>
                          {getVersionBadge(version)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {version.editReason || 'No edit reason provided'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            {formatDistanceToNow(new Date(version.createdAt), {
                              addSuffix: true,
                            })}
                          </span>
                          {version.diff?.charactersChanged && (
                            <span>
                              {formatCharacterChange(
                                version.diff.charactersChanged
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadSpecificVersion(version.versionNumber)}
                          disabled={isLoadingVersion}
                        >
                          View
                        </Button>
                        {version.versionNumber !== currentVersion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRestoreConfirm(version)}
                            disabled={isRestoring}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                    {index < versions.length - 1 && <Separator className="mt-4" />}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Version {versionToRestore?.versionNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the current discussion content with version{' '}
              {versionToRestore?.versionNumber}. The current content will be saved
              as a new version before restoring.
              <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">Edit reason:</span>{' '}
                  {versionToRestore?.editReason || 'No reason provided'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Created{' '}
                  {versionToRestore?.createdAt &&
                    formatDistanceToNow(new Date(versionToRestore.createdAt), {
                      addSuffix: true,
                    })}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRestoring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRestoreVersion(versionToRestore?.versionNumber)}
              disabled={isRestoring}
            >
              {isRestoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
